import { ReactNode, useCallback, useMemo } from 'react';
import styled from 'styled-components';

import FlagCircleUk from '@assets/icons/FlagCircleUk';
import FlagCircleUsa from '@assets/icons/FlagCircleUsa';
import { UnstyledButton } from '@components/UnstyledButton/UnstyledButton';
import {
  SELECTED_COUNTRY_UK,
  SELECTED_COUNTRY_USA,
} from '@lib/utils/constants/selectedCountry';

import { useSignupPageContext } from '../../SignupPageContext';

export const CountryFlags = (): JSX.Element => {
  const { selectedCountry, setSelectedCountry } = useSignupPageContext();
  return (
    <StyledContainer>
      {FLAG_LIST.map((flag) => (
        <FlagCard
          selectedCountry={selectedCountry}
          key={flag.label}
          label={flag.label}
          icon={flag.icon}
          setSelectedCountry={setSelectedCountry}
          value={flag.value}
        />
      ))}
    </StyledContainer>
  );
};

type FlagCardProps = FlagCardType & {
  selectedCountry: SelectedCountryType;
  setSelectedCountry: ReactStateUpdater<SelectedCountryType>;
};
const FlagCard = ({
  label,
  icon,
  setSelectedCountry,
  value,
  selectedCountry,
}: FlagCardProps) => {
  const onSelectFlag = useCallback(
    () => setSelectedCountry(value),
    [setSelectedCountry, value]
  );
  const isSelected = useMemo(
    () => selectedCountry === value,
    [selectedCountry, value]
  );
  return (
    <StyledFlagCard isSelected={isSelected} onClick={onSelectFlag}>
      <div className='flag-icon-container'>{icon}</div>
      <p className='country-name'>{label}</p>
    </StyledFlagCard>
  );
};

type FlagCardType = {
  label: string;
  icon: ReactNode;
  value: ProductionCountry;
};
const FLAG_LIST: FlagCardType[] = [
  {
    label: 'United States of America',
    icon: <FlagCircleUsa />,
    value: SELECTED_COUNTRY_USA,
  },
  {
    label: 'United Kingdom',
    icon: <FlagCircleUk />,
    value: SELECTED_COUNTRY_UK,
  },
];

const StyledContainer = styled.div`
  width: 100%;
  display: flex;
  gap: 16px;
  height: 84px;
`;

type StyledFlagCardProps = {
  isSelected: boolean;
};
const StyledFlagCard = styled(UnstyledButton)<StyledFlagCardProps>`
  border: ${({ theme, isSelected }) =>
    isSelected
      ? `2px solid ${theme.palette.primary[0]}`
      : `1px solid ${theme.palette.stroke[0]}`};
  border-radius: 8px;
  gap: 12px;
  justify-content: center;
  flex: 1;
  padding: ${({ isSelected }) => (isSelected ? '0 10px' : '0 11px')};
  background-color: ${({ isSelected, theme }) =>
    isSelected ? theme.palette.color[14] : 'transparent'};

  div.flag-icon-container {
    display: flex;
  }

  p.country-name {
    margin: 0;
    line-height: 16px;
    font-size: 12px;
    font-weight: 600;
  }
`;
